import React, { useState, useEffect } from 'react'
import { categoryAPI } from '../services/api'

const CategoryDropdown = ({
  value,
  onChange,
  placeholder = 'Select a category',
  parentId = '',
  type = 'template',
  isActive = true,
  disabled = false,
  className = '',
  showSubcategories = false,
  onSubcategoryChange,
  subcategoryValue = '',
  subcategoryPlaceholder = 'Select a subcategory',
  error = null,
  label = 'Category',
  required = false
}) => {
  const [categories, setCategories] = useState([])
  const [subcategories, setSubcategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingSubcategories, setLoadingSubcategories] = useState(false)
  const [fetchError, setFetchError] = useState(null)

  // Fetch categories
  useEffect(() => {
    fetchCategories()
  }, [parentId, type, isActive])

  // Fetch subcategories when parent category changes
  useEffect(() => {
    if (showSubcategories && value) {
      fetchSubcategories(value)
    } else {
      setSubcategories([])
    }
  }, [value, showSubcategories])

  const fetchCategories = async () => {
    try {
      setLoading(true)
      setFetchError(null)
      
      const params = {
        parent_id: parentId,
        type: type,
        is_active: isActive
      }
      
      const response = await categoryAPI.getAll(params)
      
      if (response.data.success) {
        const categoriesData = response.data.data || []
        // Sort categories by name
        const sortedCategories = [...categoriesData].sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase()
          const nameB = String(b.name || '').toLowerCase()
          return nameA.localeCompare(nameB)
        })
        setCategories(sortedCategories)
      } else {
        setFetchError('Failed to load categories')
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
      setFetchError('Failed to load categories')
    } finally {
      setLoading(false)
    }
  }

  const fetchSubcategories = async (parentcategory_id) => {
    try {
      setLoadingSubcategories(true)
      
      const response = await categoryAPI.getSubCategories(parentcategory_id)
      
      if (response.data.success) {
        const subcategoriesData = response.data.data || []
        // Sort subcategories by name
        const sortedSubcategories = [...subcategoriesData].sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase()
          const nameB = String(b.name || '').toLowerCase()
          return nameA.localeCompare(nameB)
        })
        setSubcategories(sortedSubcategories)
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error)
    } finally {
      setLoadingSubcategories(false)
    }
  }

  const handleCategoryChange = (e) => {
    const selectedValue = e.target.value
    onChange(selectedValue)
    
    // Reset subcategory when parent changes
    if (showSubcategories && onSubcategoryChange) {
      onSubcategoryChange('')
    }
  }

  const handleSubcategoryChange = (e) => {
    if (onSubcategoryChange) {
      onSubcategoryChange(e.target.value)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label} {required && '*'}
          </label>
          <div className="relative">
            <select
              disabled
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed ${className}`}
            >
              <option>Loading categories...</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Main Category Dropdown */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && '*'}
        </label>
        <div className="relative">
          <select
            value={value}
            onChange={handleCategoryChange}
            disabled={disabled || loading}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              error ? 'border-red-300' : 'border-gray-300'
            } ${disabled || loading ? 'bg-gray-100 cursor-not-allowed' : ''} ${className}`}
            required={required}
          >
            <option value="">
              {loading ? 'Loading categories...' : placeholder}
            </option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          {loading && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            </div>
          )}
        </div>
        {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
        {fetchError && <p className="text-sm text-red-600 mt-1">{fetchError}</p>}
      </div>

      {/* Subcategory Dropdown */}
      {showSubcategories && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Subcategory
          </label>
          <div className="relative">
            <select
              value={subcategoryValue}
              onChange={handleSubcategoryChange}
              disabled={!value || loadingSubcategories}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !value || loadingSubcategories ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
            >
              <option value="">
                {!value 
                  ? 'Select a category first' 
                  : loadingSubcategories 
                    ? 'Loading subcategories...' 
                    : subcategoryPlaceholder
                }
              </option>
              {subcategories.map(subcategory => (
                <option key={subcategory.id} value={subcategory.id}>
                  {subcategory.name}
                </option>
              ))}
            </select>
            {loadingSubcategories && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default CategoryDropdown
